import { FilterResults } from '@api/api/screener';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { useQuery } from '@tanstack/react-query';
import { ActiveFilterOptions } from '@terminal/components/filters/Filters';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { mapFilters } from '@terminal/views/screener/use-screener-store/filter-utils';

import { fetchFilteredEntitiesTagsOnly } from './entity-screener-service/entity-screener-service';
import { hasUsableScreenerFilters } from './entity-screener-service/utils';

export const QUERY_KEY_SCREENER_ENTITIES_TAGS =
  'QUERY_KEY_SCREENER_ENTITIES_TAGS';

export const getScreenerEntitiesTagsQueryConfig = ({
  filterOptions,
  defaultOptionsMapped = {},
  isScreenerExclusionEnabled = false,
}: {
  filterOptions: ActiveFilterOptions[];
  isScreenerExclusionEnabled?: boolean;
  defaultOptionsMapped?: Record<
    string,
    {
      values: Record<string, unknown>[];
      children?: Record<string, unknown>[];
    }
  >;
}) => {
  const staleTime = getTimeTillEndOfDayMs();

  const filterOptionsMapped = {
    ...defaultOptionsMapped,
    ...mapFilters(filterOptions, isScreenerExclusionEnabled),
  };
  const enabled = hasUsableScreenerFilters(filterOptionsMapped);

  return {
    queryKey: [QUERY_KEY_SCREENER_ENTITIES_TAGS, filterOptionsMapped],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchFilteredEntitiesTagsOnly(filterOptionsMapped, signal),
    staleTime,
    enabled,
  };
};

export const useScreenerEntitiesTags = ({
  filterOptions,
  defaultOptionsMapped,
}: {
  filterOptions: ActiveFilterOptions[];
  defaultOptionsMapped?: Record<
    string,
    {
      values: Record<string, unknown>[];
      children?: Record<string, unknown>[];
    }
  >;
}) => {
  const isScreenerExclusionEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4767-screener-exclusion')
  );
  const queryConfig = getScreenerEntitiesTagsQueryConfig({
    filterOptions,
    defaultOptionsMapped,
    isScreenerExclusionEnabled,
  });

  return useQuery<FilterResults>(queryConfig);
};

export const mapEntityTagsToDefaultOptions = (entityTags: string[]) => {
  return {
    entity_tag: {
      values: [
        {
          tags: entityTags,
        },
      ],
      children: [],
    },
  };
};
